import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";

/**
 * "Certify this discovery" - a 2-question verification lock at the end of an
 * article. Questions are sourced from the existing `quizzes` table (matched by
 * href). Visitor requests never generate paid content. Missing questions are
 * created by the bounded publishing worker before the article is exposed.
 */

export type CertifyQuestion = {
  id: string;
  question: string;
  options: string[];
  answerIndex: number;
  explanation: string;
};

function normalise(row: any): CertifyQuestion | null {
  const options = Array.isArray(row?.options) ? row.options.map((o: unknown) => String(o)) : [];
  if (options.length < 2) return null;
  const idx = Number(row?.answer_index ?? row?.answerIndex ?? 0);
  if (!Number.isInteger(idx) || idx < 0 || idx >= options.length) return null;
  return {
    id: String(row.id ?? crypto.randomUUID()),
    question: String(row.question ?? "").trim(),
    options,
    answerIndex: idx,
    explanation: String(row.explanation ?? "").trim(),
  };
}

export const getCertifyQuestions = createServerFn({ method: "POST" })
  .inputValidator((d) => z.object({ slug: z.string().min(1).max(200) }).parse(d))
  .handler(async ({ data }): Promise<{ questions: CertifyQuestion[] }> => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const href = `/discovery/${data.slug}`;

    const { data: existing } = await supabaseAdmin
      .from("quizzes")
      .select("id, question, options, answer_index, explanation")
      .eq("href", href)
      .order("sort_order", { ascending: true })
      .limit(4);

    const pool = (existing ?? [])
      .map(normalise)
      .filter((q): q is CertifyQuestion => q !== null && q.question.length > 0);

    return { questions: pool.slice(0, 2) };
  });
